import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { Mic, MicOff, Sparkles, ArrowRight, ArrowLeft, ExternalLink, CheckCircle, ChevronDown, ChevronRight, Clock, X } from "lucide-react";
import contentInterviewService, { ContentInterview as ContentInterviewType, ContentInterviewTopic } from "../../services/contentInterviewService";
import selfInterviewService, { SelfInterview as SelfInterviewType } from "../../services/selfInterviewService";
import onboardingService from "../../services/onboardingService";
import { getAgency } from "../../services/agencyService";
import TopicChip from "./TopicChip";
import PricingCard from "./PricingCard";

type Step = "intro" | "topics" | "homework" | "pricing" | "booking";

const isLocal = window.location.hostname === "localhost";
const STRIPE_SINGLE_LINK = isLocal
  ? "https://buy.stripe.com/14AeVegvpdTu4JE516cEw13"
  : "https://buy.stripe.com/dRmcN62EzcPq5NI8dicEw0Z";
const STRIPE_PACK_LINK = isLocal
  ? "https://buy.stripe.com/14AeVegvpdTu4JE516cEw13"
  : "https://buy.stripe.com/6oU3cw1Av02E6RMeBGcEw12";

const ContentInterview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>("intro");
  const [interview, setInterview] = useState<ContentInterviewType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pastInterviews, setPastInterviews] = useState<ContentInterviewType[]>([]);
  const [selfInterviews, setSelfInterviews] = useState<SelfInterviewType[]>([]);
  const [sessionsPage, setSessionsPage] = useState(0);

  // Topics step
  const [topics, setTopics] = useState<ContentInterviewTopic[]>([]);
  const [topicInput, setTopicInput] = useState("");
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [blueprints, setBlueprints] = useState<any[]>([]);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(null);

  // Homework step
  const [expandedTopic, setExpandedTopic] = useState<number | null>(0);
  const [voiceTargetTopic, setVoiceTargetTopic] = useState<number | null>(null);

  // Pricing step
  const [selectedPlan, setSelectedPlan] = useState<"single" | "pack">("single");

  // Speech recognition
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const highlightProfessional = new URLSearchParams(location.search).get("highlight") === "professional";

  // Load blueprints and past interviews on mount
  useEffect(() => {
    loadBlueprints();
    loadPastInterviews();
    loadSelfInterviews();
  }, []);

  // Handle Stripe payment redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");

    if (payment === "success") {
      const storedId = localStorage.getItem("content_interview_id");
      const storedPlan = localStorage.getItem("content_interview_plan");
      if (storedId) {
        handlePaymentReturn(parseInt(storedId), storedPlan || "single");
      }
    }
  }, [location.search]);

  // Append transcript to topic input or homework notes
  useEffect(() => {
    if (!transcript) return;
    if (voiceTargetTopic !== null) {
      handleUpdateTopicNotes(voiceTargetTopic, transcript);
    } else {
      setTopicInput(transcript);
    }
  }, [transcript]);

  const loadBlueprints = async () => {
    try {
      const data = await onboardingService.listOnboardingBlueprints();
      setBlueprints(data);
      if (data.length > 0) {
        try {
          const { getPrimaryBlueprint } = await import("../../services/organizationService");
          const primary = await getPrimaryBlueprint();
          if (primary.id && data.some((bp: any) => bp.id === primary.id)) {
            setSelectedBlueprintId(primary.id);
          } else {
            setSelectedBlueprintId(data[0].id);
          }
        } catch {
          setSelectedBlueprintId(data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load blueprints:", err);
    }
  };

  const loadPastInterviews = async () => {
    try {
      const data = await contentInterviewService.list();
      setPastInterviews(data);
    } catch (err) {
      console.error("Failed to load past interviews:", err);
    }
  };

  const loadSelfInterviews = async () => {
    try {
      const data = await selfInterviewService.list();
      setSelfInterviews(data);
    } catch (err) {
      console.error("Failed to load self interviews:", err);
    }
  };

  const handlePaymentReturn = async (interviewId: number, plan: string) => {
    try {
      const updated = await contentInterviewService.confirmPayment(
        interviewId,
        plan,
        "stripe_payment_link"
      );
      setInterview(updated);
      setCurrentStep("booking");
      loadPastInterviews();
      // Clean up localStorage and URL
      localStorage.removeItem("content_interview_id");
      localStorage.removeItem("content_interview_plan");
      navigate("/content-interviews", { replace: true });
    } catch (err) {
      console.error("Failed to confirm payment:", err);
      setError("Payment confirmation failed. Please contact support.");
    }
  };

  const handleGetStarted = async () => {
    setLoading(true);
    setError("");
    try {
      const created = await contentInterviewService.create();
      setInterview(created);
      setCurrentStep("topics");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to start. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopic = () => {
    const title = topicInput.trim();
    if (!title || topics.length >= 10) return;
    setTopics([...topics, { title, source: "manual", notes: "", category: "" }]);
    setTopicInput("");
    resetTranscript();
  };

  const handleRemoveTopic = (index: number) => {
    setTopics(topics.filter((_, i) => i !== index));
  };

  const handleSuggestTopics = async () => {
    if (!selectedBlueprintId) return;
    setSuggestLoading(true);
    try {
      const suggested = await contentInterviewService.suggestTopics(selectedBlueprintId);
      // Add suggestions up to max 10
      const remaining = 10 - topics.length;
      const toAdd = suggested.slice(0, remaining);
      setTopics([...topics, ...toAdd]);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to suggest topics.");
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleTopicsNext = async () => {
    if (!interview || topics.length === 0) return;
    setLoading(true);
    try {
      await contentInterviewService.update(interview.id, {
        topics,
        status: "Topics Selected",
      });
      setCurrentStep("homework");
    } catch (err: any) {
      setError("Failed to save topics.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTopicNotes = (index: number, notes: string) => {
    const updated = [...topics];
    updated[index] = { ...updated[index], notes };
    setTopics(updated);
  };

  const handleHomeworkNext = async () => {
    if (!interview) return;
    setLoading(true);
    try {
      await contentInterviewService.update(interview.id, {
        topics,
        status: "Homework Done",
      });
      setCurrentStep("pricing");
    } catch (err: any) {
      setError("Failed to save notes.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (!interview) return;
    const url = selectedPlan === "pack" ? STRIPE_PACK_LINK : STRIPE_SINGLE_LINK;
    // Store interview context in localStorage so we can retrieve it after Stripe redirects back
    localStorage.setItem("content_interview_id", String(interview.id));
    localStorage.setItem("content_interview_plan", selectedPlan);
    window.location.href = url;
  };

  // ----- Render Steps -----

  const renderIntro = () => (
    <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center", padding: "60px 20px" }}>
      <h1 style={{ fontSize: "32px", fontWeight: 700, marginBottom: "12px", color: "#111827" }}>
        Content Interviews
      </h1>

      <p style={{ fontSize: "16px", color: "#6B7280", lineHeight: 1.7, marginBottom: "32px" }}>
        Create <strong>original content</strong> by sharing your expertise — choose how you'd like to contribute.
      </p>

      <div style={{ display: "flex", gap: "20px", marginBottom: "32px", flexWrap: "wrap", justifyContent: "center" }}>
        {/* AI Self-Interview card */}
        <div
          onClick={() => navigate("/self-interviews")}
          style={{
            flex: "1 1 280px", maxWidth: "340px",
            backgroundColor: "#FFFFFF", borderRadius: "12px", padding: "28px 24px",
            border: "2px solid #C7D2FE", cursor: "pointer",
            textAlign: "left", transition: "border-color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6366F1")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#C7D2FE")}
        >
          <div style={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "#EEF2FF", display: "flex", alignItems: "center",
            justifyContent: "center", marginBottom: "16px",
          }}>
            <Sparkles size={20} color="#6366F1" />
          </div>
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>
            AI Self-Interview
          </h3>
          <p style={{ fontSize: "14px", color: "#6B7280", lineHeight: 1.5, marginBottom: "16px" }}>
            Answer AI-generated questions at your own pace; we'll turn your responses into newsletter content. Takes ~15 minutes. Free and unlimited.
          </p>
          <span style={{
            fontSize: "13px", fontWeight: 600, color: "#6366F1",
            display: "inline-flex", alignItems: "center", gap: "4px",
          }}>
            Start a session <ArrowRight size={14} />
          </span>
        </div>

        {/* Professional Interview card */}
        <div
          onClick={handleGetStarted}
          style={{
            flex: "1 1 280px", maxWidth: "340px",
            backgroundColor: "#FFFFFF", borderRadius: "12px", padding: "28px 24px",
            border: `2px solid ${highlightProfessional ? "#6366F1" : "#E5E7EB"}`,
            boxShadow: highlightProfessional ? "0 0 0 3px rgba(99, 102, 241, 0.15)" : "none",
            cursor: loading ? "not-allowed" : "pointer",
            textAlign: "left", transition: "border-color 0.2s, box-shadow 0.2s",
            opacity: loading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6366F1")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = highlightProfessional ? "#6366F1" : "#E5E7EB")}
        >
          <div style={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "#F9FAFB", display: "flex", alignItems: "center",
            justifyContent: "center", marginBottom: "16px",
          }}>
            <CheckCircle size={20} color="#6366F1" />
          </div>
          <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>
            Professional Interview
          </h3>
          <p style={{ fontSize: "14px", color: "#6B7280", lineHeight: 1.5, marginBottom: "16px" }}>
            1-hour call with {getAgency() === 'authoritydigest' ? 'our team' : 'Evan'}. We produce 3-7 pieces of polished content. Starting at $249/session.
          </p>
          <span style={{
            fontSize: "13px", fontWeight: 600, color: "#6366F1",
            display: "inline-flex", alignItems: "center", gap: "4px",
          }}>
            {loading ? "Starting..." : "Get started"} <ArrowRight size={14} />
          </span>
        </div>
      </div>

      {error && <p style={{ color: "#EF4444", marginTop: "12px" }}>{error}</p>}

      {/* Past Sessions — unified list of AI self-interviews + professional interviews */}
      {(selfInterviews.length > 0 || pastInterviews.length > 0) && (() => {
        type UnifiedSession = { type: "ai"; date: string; data: SelfInterviewType } | { type: "professional"; date: string; data: ContentInterviewType };
        const unified: UnifiedSession[] = [
          ...selfInterviews.map((si) => ({ type: "ai" as const, date: si.created_at, data: si })),
          ...pastInterviews.map((pi) => ({ type: "professional" as const, date: pi.created_at, data: pi })),
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const pageSize = 3;
        const totalPages = Math.ceil(unified.length / pageSize);
        const page = sessionsPage;
        const paginated = unified.slice(page * pageSize, (page + 1) * pageSize);

        return (
        <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "left" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "12px" }}>
            Sessions
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
            {paginated.map((item, idx) => {
              const date = "Created " + new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" });

              if (item.type === "ai") {
                const si = item.data;
                const answered = si.questions?.filter((q: any) => q.answer_text).length || 0;
                const total = si.questions?.length || 0;
                const statusColor = si.status === "Completed" ? "#059669" : si.status === "Saved" ? "#059669" : "#F59E0B";
                const statusBg = si.status === "Completed" ? "#ECFDF5" : si.status === "Saved" ? "#ECFDF5" : "#FFFBEB";
                const statusLabel = si.status === "Completed" ? "Completed" : si.status === "Saved" ? "Saved" : "In Progress";
                return (
                  <div
                    key={`ai-${si.id}`}
                    onClick={() => navigate(`/self-interviews/${si.id}`)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 16px", borderRadius: "10px",
                      border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                      cursor: "pointer", transition: "border-color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6366F1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E7EB")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Sparkles size={16} color="#6366F1" />
                      <div>
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#111827" }}>{date}</span>
                        <span style={{ fontSize: "13px", color: "#9CA3AF", marginLeft: "8px" }}>{answered}/{total} answered</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "11px", color: "#6366F1", backgroundColor: "#EEF2FF", padding: "2px 8px", borderRadius: "6px", fontWeight: 500 }}>AI</span>
                      <span style={{ fontSize: "12px", fontWeight: 500, color: statusColor, backgroundColor: statusBg, padding: "2px 8px", borderRadius: "6px" }}>{statusLabel}</span>
                      <ArrowRight size={14} color="#9CA3AF" />
                    </div>
                  </div>
                );
              } else {
                const pi = item.data;
                const topicCount = pi.topics?.length || 0;
                return (
                  <div
                    key={`pro-${pi.id}`}
                    onClick={() => navigate("/content-interviews/list")}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 16px", borderRadius: "10px",
                      border: "1px solid #E5E7EB", backgroundColor: "#FFFFFF",
                      cursor: "pointer", transition: "border-color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6366F1")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E7EB")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <CheckCircle size={16} color="#6366F1" />
                      <div>
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#111827" }}>{date}</span>
                        <span style={{ fontSize: "13px", color: "#9CA3AF", marginLeft: "8px" }}>{topicCount} topic{topicCount !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "11px", color: "#7C3AED", backgroundColor: "#F5F3FF", padding: "2px 8px", borderRadius: "6px", fontWeight: 500 }}>Professional</span>
                      <span style={{ fontSize: "12px", fontWeight: 500, color: "#059669", backgroundColor: "#ECFDF5", padding: "2px 8px", borderRadius: "6px" }}>{pi.status}</span>
                      <ArrowRight size={14} color="#9CA3AF" />
                    </div>
                  </div>
                );
              }
            })}
          </div>
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
              <button
                onClick={() => setSessionsPage(Math.max(0, page - 1))}
                disabled={page === 0}
                style={{
                  padding: "6px 12px", borderRadius: "6px",
                  border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                  fontSize: "13px", color: page === 0 ? "#D1D5DB" : "#374151",
                  cursor: page === 0 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: "13px", color: "#6B7280" }}>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setSessionsPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                style={{
                  padding: "6px 12px", borderRadius: "6px",
                  border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                  fontSize: "13px", color: page >= totalPages - 1 ? "#D1D5DB" : "#374151",
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );

  const renderTopics = () => (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>Choose Your Topics</h2>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        Select up to 10 topics you'd like to discuss. Type them in, use voice, or let AI suggest from your Blueprint.
      </p>

      {/* Manual input */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <input
          type="text"
          value={topicInput}
          onChange={(e) => setTopicInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddTopic()}
          placeholder="Type a topic..."
          style={{
            flex: 1, padding: "10px 14px", borderRadius: "8px",
            border: "1px solid #D1D5DB", fontSize: "14px", outline: "none",
          }}
        />
        {browserSupportsSpeechRecognition && (
          <button
            onClick={() => {
              if (listening) {
                SpeechRecognition.stopListening();
                setVoiceTargetTopic(null);
              } else {
                setVoiceTargetTopic(null);
                resetTranscript();
                SpeechRecognition.startListening({ continuous: true });
              }
            }}
            style={{
              padding: "10px 12px", borderRadius: "8px", border: "1px solid #D1D5DB",
              backgroundColor: listening ? "#FEE2E2" : "#FFFFFF", cursor: "pointer",
              display: "flex", alignItems: "center",
            }}
          >
            {listening ? <MicOff size={18} color="#EF4444" /> : <Mic size={18} color="#6B7280" />}
          </button>
        )}
        <button
          onClick={handleAddTopic}
          disabled={!topicInput.trim() || topics.length >= 10}
          style={{
            padding: "10px 16px", borderRadius: "8px", border: "none",
            backgroundColor: "#6366F1", color: "#FFFFFF", fontWeight: 600,
            cursor: !topicInput.trim() || topics.length >= 10 ? "not-allowed" : "pointer",
            opacity: !topicInput.trim() || topics.length >= 10 ? 0.5 : 1,
          }}
        >
          Add
        </button>
      </div>

      {/* Blueprint selector + suggest button */}
      {blueprints.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
          <select
            value={selectedBlueprintId || ""}
            onChange={(e) => setSelectedBlueprintId(parseInt(e.target.value))}
            style={{
              padding: "8px 12px", borderRadius: "8px",
              border: "1px solid #D1D5DB", fontSize: "14px",
              color: "#374151", cursor: "pointer",
            }}
          >
            {blueprints.map((bp: any) => (
              <option key={bp.id} value={bp.id}>{bp.title}</option>
            ))}
          </select>
          <button
            onClick={handleSuggestTopics}
            disabled={suggestLoading || topics.length >= 10}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "8px 16px", borderRadius: "8px",
              border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
              fontSize: "14px", color: "#6366F1", fontWeight: 500,
              cursor: suggestLoading || topics.length >= 10 ? "not-allowed" : "pointer",
            }}
          >
            <Sparkles size={16} />
            {suggestLoading ? "Generating topics (this may take a minute)..." : "Generate Topics"}
          </button>
        </div>
      )}

      {/* Topic cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "24px" }}>
        {topics.map((topic, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            padding: "14px 16px", borderRadius: "10px",
            backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB",
          }}>
            <p style={{ fontSize: "14px", color: "#374151", lineHeight: 1.5, margin: 0, flex: 1, paddingRight: "12px" }}>
              {topic.title}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              {topic.category && (
                <span style={{
                  fontSize: "11px", color: "#6B7280", backgroundColor: "#E5E7EB",
                  padding: "2px 8px", borderRadius: "6px", whiteSpace: "nowrap",
                }}>
                  {topic.category}
                </span>
              )}
              <button
                onClick={() => handleRemoveTopic(i)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#9CA3AF", padding: "2px", display: "flex",
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {topics.length > 0 && (
        <p style={{ fontSize: "13px", color: "#9CA3AF", marginBottom: "24px" }}>
          {topics.length}/10 topics selected
        </p>
      )}

      {/* Navigation */}
      <div className="ci-nav-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
        <button
          onClick={() => setCurrentStep("intro")}
          style={{
            padding: "10px 20px", borderRadius: "8px", border: "1px solid #D1D5DB",
            backgroundColor: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          onClick={handleTopicsNext}
          disabled={topics.length === 0 || loading}
          style={{
            padding: "10px 20px", borderRadius: "8px", border: "none",
            backgroundColor: "#6366F1", color: "#FFFFFF", fontWeight: 600,
            cursor: topics.length === 0 || loading ? "not-allowed" : "pointer",
            opacity: topics.length === 0 || loading ? 0.5 : 1,
            display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          {loading ? "Saving..." : "Next: Add Notes"} <ArrowRight size={16} />
        </button>
      </div>
      {error && <p style={{ color: "#EF4444", marginTop: "12px" }}>{error}</p>}
    </div>
  );

  const toggleVoiceForTopic = (index: number) => {
    if (listening && voiceTargetTopic === index) {
      SpeechRecognition.stopListening();
      setVoiceTargetTopic(null);
    } else {
      if (listening) SpeechRecognition.stopListening();
      setVoiceTargetTopic(index);
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true });
    }
  };

  const renderHomework = () => (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>Topic Notes</h2>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        Expand a topic to add notes, talking points, or context. This helps Evan prepare and make the most of your call.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "32px" }}>
        {topics.map((topic, i) => {
          const isExpanded = expandedTopic === i;
          const isRecording = listening && voiceTargetTopic === i;
          return (
            <div key={i} style={{
              borderRadius: "8px", border: "1px solid #E5E7EB", backgroundColor: "#FAFAFA",
              overflow: "hidden",
            }}>
              <button
                onClick={() => setExpandedTopic(isExpanded ? null : i)}
                style={{
                  width: "100%", padding: "14px 16px", border: "none", backgroundColor: "transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", textAlign: "left",
                }}
              >
                {isExpanded
                  ? <ChevronDown size={16} color="#6B7280" />
                  : <ChevronRight size={16} color="#6B7280" />
                }
                <strong style={{ fontSize: "15px", flex: 1 }}>{topic.title}</strong>
                {topic.category && (
                  <span style={{
                    fontSize: "11px", color: "#9CA3AF", backgroundColor: "#E5E7EB",
                    padding: "1px 6px", borderRadius: "8px",
                  }}>
                    {topic.category}
                  </span>
                )}
                {topic.notes && !isExpanded && (
                  <span style={{ fontSize: "12px", color: "#9CA3AF" }}>has notes</span>
                )}
              </button>
              {isExpanded && (
                <div style={{ padding: "0 16px 16px" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    <textarea
                      value={topic.notes}
                      onChange={(e) => handleUpdateTopicNotes(i, e.target.value)}
                      placeholder="Add notes, context, or talking points (optional)..."
                      rows={3}
                      style={{
                        flex: 1, padding: "10px", borderRadius: "6px",
                        border: "1px solid #D1D5DB", fontSize: "14px",
                        resize: "vertical", fontFamily: "inherit", outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {browserSupportsSpeechRecognition && (
                      <button
                        onClick={() => toggleVoiceForTopic(i)}
                        style={{
                          padding: "10px 12px", borderRadius: "8px", border: "1px solid #D1D5DB",
                          backgroundColor: isRecording ? "#FEE2E2" : "#FFFFFF", cursor: "pointer",
                          display: "flex", alignItems: "center", flexShrink: 0,
                        }}
                        title={isRecording ? "Stop recording" : "Record voice notes"}
                      >
                        {isRecording
                          ? <MicOff size={18} color="#EF4444" />
                          : <Mic size={18} color="#6B7280" />
                        }
                      </button>
                    )}
                  </div>
                  {isRecording && (
                    <p style={{ fontSize: "12px", color: "#EF4444", marginTop: "6px" }}>
                      Recording... click the mic to stop
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="ci-nav-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
        <button
          onClick={() => setCurrentStep("topics")}
          style={{
            padding: "10px 20px", borderRadius: "8px", border: "1px solid #D1D5DB",
            backgroundColor: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          onClick={handleHomeworkNext}
          disabled={loading}
          style={{
            padding: "10px 20px", borderRadius: "8px", border: "none",
            backgroundColor: "#6366F1", color: "#FFFFFF", fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.5 : 1,
            display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          {loading ? "Saving..." : "Next: Pricing"} <ArrowRight size={16} />
        </button>
      </div>
      {error && <p style={{ color: "#EF4444", marginTop: "12px" }}>{error}</p>}
    </div>
  );

  const renderPricing = () => (
    <div style={{ maxWidth: "640px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>Choose Your Plan</h2>
      <p style={{ color: "#6B7280", marginBottom: "32px" }}>
        Select a plan and proceed to payment.
      </p>

      <div style={{ display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" }}>
        <PricingCard
          title="Single Session"
          price="$349"
          perSession="$349 per session"
          selected={selectedPlan === "single"}
          onSelect={() => setSelectedPlan("single")}
        />
        <PricingCard
          title="4-Session Pack"
          price="$996"
          perSession="$249 per session"
          savings="Save $400"
          selected={selectedPlan === "pack"}
          onSelect={() => setSelectedPlan("pack")}
        />
      </div>

      <div className="ci-nav-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
        <button
          onClick={() => setCurrentStep("homework")}
          style={{
            padding: "10px 20px", borderRadius: "8px", border: "1px solid #D1D5DB",
            backgroundColor: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={handlePayment}
            style={{
              padding: "14px 28px", borderRadius: "8px", border: "none",
              backgroundColor: "#6366F1", color: "#FFFFFF", fontWeight: 600, fontSize: "16px",
              cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
            }}
          >
            Pay {selectedPlan === "pack" ? "$996" : "$349"} <ExternalLink size={16} />
          </button>
          {window.location.hostname === "localhost" && interview && (
            <button
              onClick={() => handlePaymentReturn(interview.id, selectedPlan)}
              style={{
                padding: "14px 28px", borderRadius: "8px", border: "2px dashed #F59E0B",
                backgroundColor: "#FFFBEB", color: "#92400E", fontWeight: 600, fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Skip Payment (Dev)
            </button>
          )}
        </div>
      </div>
      {error && <p style={{ color: "#EF4444", marginTop: "12px" }}>{error}</p>}
    </div>
  );

  const renderBooking = () => (
    <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center", padding: "60px 20px" }}>
      <CheckCircle size={48} color="#10B981" style={{ marginBottom: "16px" }} />
      <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>Payment Confirmed!</h2>
      <p style={{ color: "#6B7280", marginBottom: "32px" }}>
        Now book your 1-hour content interview with {getAgency() === 'authoritydigest' ? 'our team' : 'Evan'}.
      </p>

      {getAgency() !== 'authoritydigest' && (
        <a
          href="https://calendly.com/evan-withpotions/evan-content-interview"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            padding: "14px 32px", fontSize: "16px", fontWeight: 600,
            backgroundColor: "#6366F1", color: "#FFFFFF", border: "none",
            borderRadius: "8px", textDecoration: "none",
          }}
        >
          Book Your Call <ExternalLink size={18} />
        </a>
      )}
      <p style={{ fontSize: "13px", color: "#9CA3AF", marginTop: "16px" }}>
        You'll receive a calendar invite and reminder emails before the call.
      </p>
    </div>
  );

  // Step renderer
  const renderStep = () => {
    switch (currentStep) {
      case "intro":
        return renderIntro();
      case "topics":
        return renderTopics();
      case "homework":
        return renderHomework();
      case "pricing":
        return renderPricing();
      case "booking":
        return renderBooking();
    }
  };

  // Progress indicator
  const steps: { key: Step; label: string }[] = [
    { key: "intro", label: "Intro" },
    { key: "topics", label: "Topics" },
    { key: "homework", label: "Notes" },
    { key: "pricing", label: "Payment" },
    { key: "booking", label: "Book" },
  ];

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div style={{ minHeight: "100%", backgroundColor: "#FFFFFF" }}>
      <style>{`
        @media (max-width: 640px) {
          .ci-nav-buttons { flex-direction: column; gap: 8px !important; }
          .ci-nav-buttons button, .ci-nav-buttons a { width: 100%; justify-content: center; text-align: center; }
        }
      `}</style>
      {/* Step indicator */}
      <div style={{
        display: "flex", justifyContent: "center", gap: "4px",
        padding: "24px 20px 0", maxWidth: "640px", margin: "0 auto",
      }}>
        {steps.map((step, i) => (
          <div key={step.key} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              height: "4px", borderRadius: "2px", marginBottom: "6px",
              backgroundColor: i <= currentIndex ? "#6366F1" : "#E5E7EB",
              transition: "background-color 0.3s ease",
            }} />
            <span style={{
              fontSize: "12px", fontWeight: i === currentIndex ? 600 : 400,
              color: i <= currentIndex ? "#6366F1" : "#9CA3AF",
            }}>
              {step.label}
            </span>
          </div>
        ))}
      </div>

      {renderStep()}
    </div>
  );
};

export default ContentInterview;
